// The delivery step's offer: "Add some of these products for [free delivery]" — a row of the products that
// bring a seller of the cart to its free delivery (store {cart_upsell}, reason "free_shipping": its own
// products whose price makes up what is missing). Each tile is its picture, price and name; a click opens
// the product's overview over the checkout (UpsellProductPanel: the buy button on top, the options, who
// made and sells it, its description), and an add from there counts as "upsell_delivery". The cart
// changing (an add) asks again: a seller reached drops out, the delivery above is priced again by the step.
import React, { useEffect, useRef, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import Img from "../ui/Img"
import PriceDisplay from "../ui/PriceDisplay"
import useT from "../hooks/useT"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { useCurrency } from "../store/currency"
import { ListProducts, LIST_FIELDS } from "../api"
import { CartUpsell as AskUpsell } from "../storeApi"
import { basePrice } from "../utils/productUtils"
import { activeDiscountPercent, applyDiscount } from "../utils/discountUtils"
import { FreeDeliveryChip } from "../ui/cart/CartUpsell"
import UpsellProductPanel from "../ui/cart/UpsellProductPanel"
import { PLACEMENTS } from "../store/placements"
import { useFreeShipping } from "../ui/cart/freeShipping"
import { formatMoney } from "../account/ui"

/**
 * A seller's row: "Add €10.16 more from SoftPress for [FREE DELIVERY]" (how far it is, whose it is — the
 * amount and the name in bold) and its products, scrolled sideways with the arrows.
 */
// `free`: the way chosen for this seller costs nothing already (its shop): the row is a good deal, not a way to free delivery
function SellerRow({ seller, paid, currency, onOpen, free = false }) {
  const t = useT()
  const row = useRef(null)
  const scroll = (direction) => row.current?.scrollBy({ left: direction * row.current.clientWidth * 0.8, behavior: "smooth" })
  const offer = seller.offer
  // the sentence with the amount and the seller in bold: the text's own order, whatever the language
  const parts = offer && !free ? t("Add %amount% more from %seller% for", "\u0001", "\u0002").split(/(\u0001|\u0002)/) : null
  return (
    <div className="flex flex-col gap-3" data-testid="delivery-upsell-seller" data-seller={seller.sellerId}>
      <div className="flex items-center gap-2">
        <span className="flex-1 min-w-0 text-[15px] text-text">
          {free ? (
            <span className="font-bold">{t`Get a good deal`}</span>
          ) : parts
            ? parts.map((part, index) =>
                part === "\u0001" ? <b key={index} className="font-bold">{formatMoney(offer.remaining, offer.currency)}</b> : part === "\u0002" ? <b key={index} className="font-semibold">{offer.sellerName}</b> : <React.Fragment key={index}>{part}</React.Fragment>
              )
            : t`Add some of these products for`}
          {!free && (
            <>
              {" "}
              <FreeDeliveryChip label={t`free delivery`} />
            </>
          )}
        </span>
        <button type="button" onClick={() => scroll(-1)} className="shrink-0 w-8 h-8 rounded-full border border-bordered flex items-center justify-center hover:border-text" aria-label={t`Previous`} data-testid="delivery-upsell-prev">
          <ChevronLeftIcon className="w-4 h-4 stroke-2" />
        </button>
        <button type="button" onClick={() => scroll(1)} className="shrink-0 w-8 h-8 rounded-full border border-bordered flex items-center justify-center hover:border-text" aria-label={t`Next`} data-testid="delivery-upsell-next">
          <ChevronRightIcon className="w-4 h-4 stroke-2" />
        </button>
      </div>
      {/* the tiles in one row, scrolled sideways (the arrows, a swipe) */}
      <div ref={row} className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1">
        {seller.products.map((product) => {
          const price = paid(product)
          return (
            <button
              key={product.pid}
              type="button"
              onClick={() => onOpen(product)}
              className="snap-start shrink-0 w-[132px] flex flex-col gap-1.5 rounded-lg border border-bordered p-2 text-left hover:border-text transition-colors"
              data-testid="delivery-upsell-item"
              data-pid={product.pid}
            >
              <Img src={product.square || product.thumb} alt={product.name} width={116} height={116} className="w-full aspect-square rounded-md overflow-hidden" />
              {price !== null && <PriceDisplay amount={price} currency={currency} variant="small" className="min-w-0" />}
              <span className="text-[13px] font-semibold leading-tight line-clamp-2">{product.name}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

const DELAY = 300

// `isFree(sellerId)`: whether the delivery chosen for that seller already costs nothing
export default function DeliveryUpsell({ cart = [], className = "", onShown, isFree = () => false }) {
  const t = useT()
  const language = useSiteLanguage()
  const { currency, convert } = useCurrency()
  const [products, setProducts] = useState([])
  const [open, setOpen] = useState(null)
  const offers = useFreeShipping(cart) || []
  const lines = cart.map((line) => ({ pid: line.pid, quantity: line.quantity || 1 }))
  const key = lines.map((line) => `${line.pid}:${line.quantity}`).join(",")

  useEffect(() => {
    if (!lines.length) return setProducts([])
    let alive = true
    const timer = setTimeout(async () => {
      try {
        const pids = [...new Set(lines.map((line) => line.pid))].reverse()
        const answer = await AskUpsell(pids, 9, lines)
        // only what brings a seller to free delivery, each with the seller it is for
        const picks = (answer?.list || []).filter((entry) => entry.reason === "free_shipping")
        const wanted = picks.map((entry) => entry.pid)
        if (!wanted.length) return alive && setProducts([])
        const listed = await ListProducts({ page: 0, pids: wanted, items_per_page: wanted.length, language, currency, owned_only: false, fields: LIST_FIELDS })
        const byPid = new Map((listed?.list || []).map((product) => [product.pid, product]))
        if (alive) setProducts(picks.map((entry) => byPid.get(entry.pid) && { ...byPid.get(entry.pid), _sellerId: String(entry.sellerId || "") }).filter(Boolean))
      } catch {
        if (alive) setProducts([])
      }
    }, DELAY)
    return () => {
      alive = false
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, language, currency])

  // the step knows whether there is anything to point at (its sticky "Free delivery" chip)
  useEffect(() => {
    onShown?.(products.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length])

  if (!products.length) return null
  const paid = (product) => {
    const base = basePrice(product)
    return base ? applyDiscount(convert(base.amount, base.currency), activeDiscountPercent(product)) : null
  }
  // one row per seller short of its free delivery (the cart's own reading of how far: useFreeShipping)
  const sellers = [...new Set(products.map((product) => product._sellerId))].map((sellerId) => ({
    sellerId,
    offer: offers.find((entry) => String(entry.sellerId) === sellerId) || null,
    products: products.filter((product) => product._sellerId === sellerId),
  }))

  return (
    <section className={`flex flex-col gap-5 ${className}`} data-testid="delivery-upsell">
      {sellers.map((seller) => (
        <SellerRow key={seller.sellerId} seller={seller} paid={paid} currency={currency} onOpen={setOpen} single={sellers.length === 1} free={isFree(seller.sellerId)} />
      ))}
      {open && <UpsellProductPanel product={open} placement={PLACEMENTS.UPSELL_DELIVERY} title={t`Product`} onClose={() => setOpen(null)} onNavigate={() => setOpen(null)} />}
    </section>
  )
}
